import { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { ContentContainer } from 'components/containers/ItemCampaignContainers';
import ObjectError from 'components/ui-elements/ObjectError';
import { ObjectNotFound } from 'components/ui-elements/ObjectNotFound';
import Spinner from 'components/ui-elements/Spinner';
import ItemCheckoutContent from 'features/checkout/item/ItemCheckoutContent';
import { useFraudSession } from 'features/checkout/payment/useFraudSession';
import { CheckoutSuccessState } from 'features/checkout/success/checkoutSuccessState';
import { useCurrentUser, usePaymentConfig } from 'providers/cache/cachedDataHooks';
import { PaymentProvider } from 'providers/PaymentProvider';
import { ShippingAddressProvider } from 'providers/ShippingAddressProvider';
import { useItemData } from 'services/item/useItemData';
import { PurchaseDto } from 'services/order/orderService.dto';
import { TransactionDto } from 'services/payment/paymentService.dto';

import { useOnSiteInitData } from './useOnSiteInitData';

export const OnSiteCheckout = () => {
  const { paymentConfig } = usePaymentConfig();
  const { currentUser } = useCurrentUser();
  const navigate = useNavigate();

  const { purchase, status } = useOnSiteInitData();
  const { loading: itemLoading, content: item, error: itemError } = useItemData(purchase?.itemId);
  useFraudSession();

  const renderWithContainer = (content: ReactElement) => <ContentContainer>{content}</ContentContainer>;

  if (status === 'PURCHASE_NOT_FOUND') return renderWithContainer(<ObjectNotFound testId={'reassigned-error'} />);

  if (status === 'UNKNOWN_ERROR') return renderWithContainer(<ObjectError testId={'unknown-error'} />);

  if (status === 'NOT_INITIALIZED' || status === 'LOGIN_REQUEST' || itemLoading || !currentUser || !paymentConfig)
    return renderWithContainer(<Spinner displayLabel={true} />);

  if (status === 'REASSIGNMENT_NEEDED' && !item) return renderWithContainer(<Spinner displayLabel={true} />);

  if (purchase?.status !== 'CONFIRMED') return renderWithContainer(<ObjectNotFound testId={'order-status-error'} />);

  if (currentUser.id === purchase.ownerId) return renderWithContainer(<ObjectNotFound testId={'owner-error'} />);

  if (itemError || !item) return renderWithContainer(<ObjectError error={itemError} testId={'loading-error'} />);

  const onPurchasePaid = (purchase: PurchaseDto, transaction: TransactionDto) => {
    const state: CheckoutSuccessState = { purchase, transaction };
    navigate('/checkout/success', { state });
  };

  return renderWithContainer(
    <PaymentProvider>
      <ShippingAddressProvider>
        <ItemCheckoutContent
          item={item}
          payForOffer={false}
          canAdjustPrice={true}
          isInitializing={status !== 'INITIALIZED'}
          existingOrder={purchase!!}
          onPurchasePaid={onPurchasePaid}
        />
      </ShippingAddressProvider>
    </PaymentProvider>
  );
};
